import { Dictionary } from 'lodash';
import { Moment } from 'moment';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Team, User } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { SchedulerEventType } from 'components/organisms/resource-management/event-types';
import {
  BulkActionPayloadMap,
  ProjectsWithMembers,
  SchedulerReference,
} from 'components/organisms/resource-management/types';
import { bulkActionsMapActions } from 'components/organisms/resource-management/utils/bulk-actions';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { allocationsBulkAction } from 'data/templates/projects/actions';
import { useTranslate } from 'hooks/use-translation';
import { TemplateAssignee } from '../types';
import { asTemplateAllocation } from '../utils/as-template-allocation';
import { useAggregationManager } from './aggregration-manager';
import { useAutoFillAllocationsFromTasks } from './autofill-from-tasks';

interface Props {
  schedulerRef: SchedulerReference;
  project: ProjectsWithMembers;
  workingDaysConfig: WorkingDaysConfiguration;
  relativeDate: Moment;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  allTasksAssignees: TemplateAssignee;
  saveNewlyAllocatedMembersToState: (actionMap: BulkActionPayloadMap) => void;
  isHoursPerWeek?: boolean;
}

interface Result {
  removeAllExistingAndAutoFillFromTasks: (silent?: boolean) => void;
}

export const useRefreshAllocationsFromTasks = (props: Props): Result => {
  const {
    schedulerRef,
    relativeDate,
    saveNewlyAllocatedMembersToState,
    isHoursPerWeek,
  } = props;
  const dispatch = useDispatch();
  const { refreshAggregation } = useAggregationManager({ schedulerRef });
  const { autoFillAllocationsFromTasks } =
    useAutoFillAllocationsFromTasks(props);
  const translate = useTranslate();

  const removeAllExistingAndAutoFillFromTasks = useCallback(
    (silent = false) => {
      const allocationEvents =
        schedulerRef.current.instance.eventStore.allRecords.filter(
          (x) => x.type === SchedulerEventType.Allocation,
        );
      const { deleteAllocation, createAllocation, actionsMap } =
        bulkActionsMapActions();

      allocationEvents.forEach((x) => {
        schedulerRef.current.instance.eventStore.remove(x);
        deleteAllocation(asAllocationPayload(x));
      });
      const newEvents = autoFillAllocationsFromTasks();
      const asAllocation = asTemplateAllocation(relativeDate, isHoursPerWeek);
      const allEvents = newEvents.map((x) => {
        const [added] = schedulerRef.current.instance.eventStore.add(x);
        const allocationAsPayload = asAllocationPayload(added);

        createAllocation(allocationAsPayload, x.id);

        return asAllocation(allocationAsPayload);
      });

      refreshAggregation(allEvents);
      dispatch(
        allocationsBulkAction({
          actionsMap,
          relativeDate,
        }),
      );
      saveNewlyAllocatedMembersToState(actionsMap);
      if (!silent) {
        toastService({
          title: translate(
            'templates.resource_management.autofill.success.title',
          ),
          message: '',
        }).success();
      }
    },
    [
      autoFillAllocationsFromTasks,
      dispatch,
      refreshAggregation,
      relativeDate,
      saveNewlyAllocatedMembersToState,
      schedulerRef,
      translate,
      isHoursPerWeek,
    ],
  );

  return {
    removeAllExistingAndAutoFillFromTasks,
  };
};
